/**
 * TechnologyTab — IN REVIEW stage — DOMAIN-ADAPTIVE
 * For biotech: AI enrichment, target profile, external IDs, therapeutic classification, mechanism, evidence cards
 * For other domains: simpler template-based fields
 */

import type { ProjectDetailProject } from '../../../types'
import { InlineNote } from '../../ui/InlineNote'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

const MOLECULE_TYPES = [
  'Small Molecule',
  'Protein',
  'mRNA',
  'Antibody',
  'Cell Therapy',
  'Gene Therapy',
  'Peptide',
  'Lipid Nanoparticle',
  'Oligonucleotide',
  'Virotherapeutic',
  'Enzyme',
  'Botanical Extract',
  'PROTAC',
]

const THERAPEUTIC_AREA_OPTIONS = [
  'Oncology',
  'Cardiovascular',
  'Immunology',
  'Neurology',
  'Metabolic',
  'Infectious Disease',
  'Rare Disease',
  'Dermatology',
  'GI/Hepatology',
  'Respiratory',
]

const MODALITY_TAGS = [
  'Agonist',
  'Antagonist',
  'Inhibitor',
  'Activator',
  'Modulator',
  'Agonist/Antagonist',
  'Partial Agonist',
]

export function TechnologyTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions,
  tabId,
}: TabContentProps) {
  // Domain-adaptive rendering
  if (domain === 'biotech') {
    return (
      <BiotechTechnologyView
        project={project}
        onFieldChange={onFieldChange}
        sectionCompletions={sectionCompletions}
        tabId={tabId}
      />
    )
  } else {
    return (
      <GenericTechnologyView
        project={project}
        domain={domain}
        onFieldChange={onFieldChange}
        sectionCompletions={sectionCompletions}
        tabId={tabId}
      />
    )
  }
}

/**
 * Biotech-specific technology view with AI enrichment and target profile
 */
function BiotechTechnologyView({
  project,
  onFieldChange,
  sectionCompletions,
  tabId,
}: {
  project: ProjectDetailProject
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}) {
  const sc = sectionCompletions
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId || '', sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId || '', sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId || '', sectionId)
  return (
    <div className="space-y-6">
      {/* AI Enrichment Trigger */}
      <div
        className="bg-[#1e1e38] border-2 border-[#10B981] rounded-xl p-5"
        style={{
          borderColor: isComplete('tech-ai-enrichment') ? 'rgba(34, 197, 94, 0.25)' : '#10B981',
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[13px] font-medium text-[#10B981]">
            AI Enrichment
          </h3>
          {/* Note: CompletableSection could wrap this, but we're keeping the special border styling */}
        </div>
        <p className="text-[13px] text-[#a8a8c0] mb-4">
          Last run: Never
        </p>
        <button
          className="
            px-4 py-2
            bg-[#10B981] text-white
            rounded-lg
            text-[13px] font-medium
            hover:bg-[#0ea575]
            transition-colors
          "
        >
          Run AI Enrichment
        </button>
      </div>

      {/* Target Profile */}
      <CompletableSection
        sectionId="tech-target-profile"
        title="Target Profile"
        isComplete={isComplete('tech-target-profile')}
        completedByName={getInfo('tech-target-profile')?.completed_by_name}
        completedAt={getInfo('tech-target-profile')?.completed_at}
        onToggle={() => toggle('tech-target-profile')}
      >
        {/* Example enriched cards */}
        <div className="grid grid-cols-2 gap-3">
          {['Gene', 'Function', 'Tractability', 'Known Drugs', 'Safety', 'Mechanism'].map(
            (field) => (
              <div
                key={field}
                className="
                  bg-[#12122a] border border-[#2a2a44]
                  rounded-lg p-3
                "
              >
                <div className="text-[12px] font-medium text-[#a8a8c0] mb-2">
                  {field}
                </div>
                <div className="text-[13px] text-[#8b8ba0]">
                  [Enriched data]
                </div>
                <div className="text-[11px] text-[#10B981] mt-1">
                  High confidence
                </div>
              </div>
            )
          )}
        </div>
      </CompletableSection>

      {/* External Database IDs */}
      <CompletableSection
        sectionId="tech-external-ids"
        title="External Database IDs"
        isComplete={isComplete('tech-external-ids')}
        completedByName={getInfo('tech-external-ids')?.completed_by_name}
        completedAt={getInfo('tech-external-ids')?.completed_at}
        onToggle={() => toggle('tech-external-ids')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Ensembl Gene ID
          </label>
          <input
            type="text"
            placeholder="ENSG..."
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            ChEMBL Target ID
          </label>
          <input
            type="text"
            placeholder="CHEMBL..."
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          />
        </div>
      </CompletableSection>

      {/* Therapeutic Classification */}
      <CompletableSection
        sectionId="tech-therapeutic"
        title="Therapeutic Classification"
        isComplete={isComplete('tech-therapeutic')}
        completedByName={getInfo('tech-therapeutic')?.completed_by_name}
        completedAt={getInfo('tech-therapeutic')?.completed_at}
        onToggle={() => toggle('tech-therapeutic')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Therapeutic Area
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          >
            <option value="">Select area</option>
            {THERAPEUTIC_AREA_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Indication
          </label>
          <input
            type="text"
            placeholder="Specific disease indication"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Therapeutic Tags
          </label>
          <div className="text-[12px] text-[#a8a8c0]">
            Multi-select tags (placeholder)
          </div>
        </div>
      </CompletableSection>

      {/* Target & Mechanism */}
      <CompletableSection
        sectionId="tech-mechanism"
        title="Target & Mechanism"
        isComplete={isComplete('tech-mechanism')}
        completedByName={getInfo('tech-mechanism')?.completed_by_name}
        completedAt={getInfo('tech-mechanism')?.completed_at}
        onToggle={() => toggle('tech-mechanism')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Molecular Target
          </label>
          <input
            type="text"
            placeholder="Target protein or pathway"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Molecule Type
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          >
            <option value="">Select type</option>
            {MOLECULE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Modality Tags
          </label>
          <div className="flex flex-wrap gap-2">
            {MODALITY_TAGS.map((tag) => (
              <button
                key={tag}
                className="
                  px-3 py-1
                  bg-[#12122a] border border-[#2a2a44]
                  rounded text-[12px] text-[#a8a8c0]
                  hover:border-[#3a3a54]
                "
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </CompletableSection>

      {/* AI-Assessed Evidence Cards */}
      <CompletableSection
        sectionId="tech-evidence"
        title="AI-Assessed Evidence"
        isComplete={isComplete('tech-evidence')}
        completedByName={getInfo('tech-evidence')?.completed_by_name}
        completedAt={getInfo('tech-evidence')?.completed_at}
        onToggle={() => toggle('tech-evidence')}
      >
        <div className="grid grid-cols-3 gap-3">
          {['MoA Status', 'Preclinical Data', 'Safety Signals'].map((metric) => (
            <div
              key={metric}
              className="
                bg-[#12122a] border border-[#2a2a44]
                rounded-lg p-3
              "
            >
              <div className="text-[12px] font-medium text-[#a8a8c0] mb-2">
                {metric}
              </div>
              <div className="text-[13px] text-[#8b8ba0]">[AI Assessment]</div>
              <div className="text-[11px] text-[#F59E0B] mt-1">
                Medium confidence
              </div>
            </div>
          ))}
        </div>
      </CompletableSection>
    </div>
  )
}

/**
 * Generic/simplified technology view for non-biotech domains
 */
function GenericTechnologyView({
  project,
  domain,
  onFieldChange,
  sectionCompletions,
  tabId,
}: {
  project: ProjectDetailProject
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}) {
  const sc = sectionCompletions
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId || '', sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId || '', sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId || '', sectionId)

  return (
    <div className="space-y-6">
      <CompletableSection
        sectionId="tech-profile"
        title={`${domain.charAt(0).toUpperCase() + domain.slice(1)} Technology Profile`}
        isComplete={isComplete('tech-profile')}
        completedByName={getInfo('tech-profile')?.completed_by_name}
        completedAt={getInfo('tech-profile')?.completed_at}
        onToggle={() => toggle('tech-profile')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Core Technology Description
          </label>
          <InlineNote
            value={project.technology_description || ''}
            onChange={(value) => onFieldChange('technology_description', value)}
            placeholder="What is the core technology?"
            minHeight={100}
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Key Features
          </label>
          <input
            type="text"
            placeholder="Unique technical features"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Technical Maturity
          </label>
          <select
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          >
            <option value="">Select level</option>
            <option value="Concept">Concept</option>
            <option value="Prototype">Prototype</option>
            <option value="Beta">Beta</option>
            <option value="Production">Production</option>
          </select>
        </div>
      </CompletableSection>
    </div>
  )
}

export default TechnologyTab
